import { useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { and, collection, onSnapshot, or, orderBy, query, Timestamp, where } from '@firebase/firestore';

import { useAuth } from '../../hooks';
import { db } from '../../firebase-config';
import { Banner } from '../admin/BannerManagement/constants.ts';
import { bannerConverter } from './bannerConverter.ts';

export const useTargetUserIdBanners = () => {
  const { user } = useAuth();
  const [banners, setBanners] = useState<Banner[]>([]);

  useEffect(() => {
    if (!user) {
      return () => {};
    }

    const now = Timestamp.now();

    const targetUserIdsQuery = query(
      collection(db, 'banners'),
      and(
        where('isActive', '==', true),
        where('targetUserIds', 'array-contains', user.uid),
        or(where('startDate', '==', null), where('startDate', '<', now)),
        or(where('endDate', '==', null), where('endDate', '>', now)),
      ),
      orderBy('createdAt', 'desc'),
    ).withConverter(bannerConverter);

    // Set up listeners for both queries
    const unsubscribes = onSnapshot(
      targetUserIdsQuery,
      (snapshot) => {
        setBanners(() => snapshot.docs.map((doc) => doc.data()));
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.error(error);
          console.error('Error in user id banner listener:', { targetUserIdsQuery, userId: user.uid });
        }

        captureException(error, {
          tags: {
            issueIn: 'useTargetUserIdBanners',
          },
          extra: {
            user,
          },
        });
      },
    );

    // Clean up listeners on unmount
    return () => {
      unsubscribes();
    };
  }, [user?.uid]);

  return banners;
};
